/* eslint-disable */
import React, { useMemo } from "react";
import ChartistGraph from "react-chartist";
import { makeStyles } from "@material-ui/core/styles";
import { CircularProgressbar, buildStyles } from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";
import Chartist from "chartist";
import Tippy from "@tippyjs/react"; // React wrapper for Tippy

import PropTypes from "prop-types";

import tippy from "tippy.js"; // IMPERATIVE TIPPY API
import { useTranslation } from "react-i18next";
import { formatNumber } from "utils/formatNumberUtils";
import "tippy.js/dist/tippy.css";

const useStyles = makeStyles((theme) => ({
  container: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
  },
  contentWrapper: {
    flex: 1,
    display: "flex",
    flexDirection: "row",
    overflow: "hidden",
  },
  chartSection: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    margin: "1px 0px 6px 10px",
  },
  chartArea: {
    flex: 1,
    overflow: "hidden",
  },
  legendContainer: {
    display: "flex",
    gap: "10px",
    marginTop: "-10px",
    flexWrap: "wrap",
    justifyContent: "center",
  },
  legendItem: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    fontSize: "11px",
    color: theme.palette.text.secondary,
  },
  progressSection: {
    width: "60px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    marginRight: 10,
    marginLeft: 2,
  },
  tableContainer: {
    margin: theme.spacing(1),
    marginLeft: theme.spacing(2),
    marginBottom: "40px",
    overflowX: "auto",
    flex: 1,
    alignContent: "center",
  },
  kpiTable: {
    borderCollapse: "collapse",
    width: "100%",
    fontSize: "0.8rem",
    lineHeight: "1.2rem",
    color: "#555",
    textAlign: "center",
  },
  kpiTableHeaderCell: {
    padding: "4px 6px",
    borderBottom: "1px solid #ddd",
    backgroundColor: "#f7f7f7",
  },
  kpiTableCell: {
    padding: "4px 6px",
    borderBottom: "1px solid #ddd",
  },
  rowHeader: {
    textAlign: "left",
    fontWeight: "bold",
    backgroundColor: "#fafafa",
  },
  "@global": {
    // Chart line thickness

    ".my-custom-chart .ct-series-a .ct-line, .my-custom-chart .ct-series-b .ct-line, .my-custom-chart .ct-series-c .ct-line":
      {
        strokeWidth: "2px !important",
      },
    ".my-custom-chart .ct-series-a .ct-line": {
      stroke: "#0B91B5 !important", // Goal
    },
    ".my-custom-chart .ct-series-b .ct-line": {
      stroke: "#28A745 !important", // Real
    },
    //".my-custom-chart .ct-series-c .ct-line": {
    //  stroke: "#FF9800 !important", // Projection

    //  strokeDasharray: "5px 5px",
    //},
    ".my-custom-chart .ct-series-a .ct-point": {
      stroke: "#0B91B5 !important",
      strokeWidth: "5px !important",
    },
    ".my-custom-chart .ct-series-b .ct-point": {
      stroke: "#28A745 !important",
      strokeWidth: "5px !important",
    },
    //".my-custom-chart .ct-series-c .ct-point": {
    //  stroke: "#FF9800 !important",
    //  strokeWidth: "5px !important",
    // strokeDasharray: "none",
    //},
  },
}));

function formatValueOneDecimal(value) {
  /*
   * Retorna "--" se o valor for nulo/indefinido ou não-numérico.
   * Se for numérico, mostra sempre 1 casa decimal
   *   2   => "2.0"
   *   2.04 => "2.0"
   *   2.05 => "2.1"
   */
  if (value == null || isNaN(value)) return "--";
  const rounded = Math.round(value * 10) / 10; // Faz o arredondamento a 1 casa
  return rounded.toFixed(1); // Exibe sempre 1 decimal
}

function MultiSeriesLineChart({
  timeAgg,
  viewMode,
  data,
  trackingMode,
  thresholdsConfig, // {Greenrange, YellowRange, ...} or {asc: {...}, Desc: {...}}
}) {
  const classes = useStyles();

  const { t } = useTranslation();

  // Decide which data object to use based on timeAgg

  const dataConfig = useMemo(() => {
    const source = data; // pai já filtra monthly/yearly

    if (!source) return { labels: [], series: [], seriesNames: [] };
    return {
      ...source,
      labels: [...(source.labels || [])],
      series: source.series?.map((arr) => [...arr]) || [],
      seriesNames: [...(source.seriesNames || [])],
    };
  }, [data]);

  // Prepare Data for Chartist

  const chartData = useMemo(
    () => ({
      labels: dataConfig.labels,
      series: dataConfig.series,
    }),
    [dataConfig]
  );

  // BASIC CHARTIST OPTIONS

  const chartOptions = useMemo(
    () => ({
      lineSmooth: Chartist.Interpolation.cardinal({ tension: 0 }),
      axisX: { showGrid: false },
      axisY: { showGrid: true },
      height: "100%",
      series: {
        "series-0": { className: "ct-series-a" },
        "series-1": { className: "ct-series-b" },
        //"series-2": { className: "ct-series-c" },
      },
    }),
    []
  );

  // Animate + Tooltips Logic

  // Animate + Tooltips + Custom Bar Drawing Logic
  const animation = useMemo(
    () => ({
      draw: (ctx) => {
        // Custom drawing for series-1 (Real) as bars:
        if (ctx.type === "point" && ctx.seriesIndex === 1) {
          // Verificação de segurança: garante que chartRect existe
          if (!ctx.chartRect || !ctx.chartRect.y2) {
            return; // Sai sem desenhar se não houver dados suficientes
          }

          // Define the bar width (adjust as needed)
          // A width relative to the space between points might be more robust,
          // but a fixed value is simpler to start.
          const barWidth = 15; // Example fixed width

          // Calculate the x coordinate so the bar is centered on the point's x position
          const x = ctx.x - barWidth / 2;

          // Calculate the bar's top y-coordinate (the data point's y)
          const y = ctx.y;

          // Calculate the bar's height: from the chart's baseline (bottom) to the point's y
          // Chartist's y-axis typically goes from top (low value) to bottom (high value)
          // So chartRect.y2 is the bottom line (usually value 0 or min value)
          const height = ctx.chartRect.y2 - ctx.y;

          // Create a rectangle (bar) using Chartist.Svg
          const bar = new Chartist.Svg(
            "rect",
            {
              x: x,
              y: y, // The top of the bar corresponds to the data point's y
              width: barWidth,
              height: Math.max(0, height), // Ensure height is not negative
              style: "stroke: none; fill: #28A745;", // Use the 'real' color, no border
            },
            "ct-bar" // Add a class for potential styling
          );

          // Append the bar to the correct group
          ctx.group.append(bar);

          // Prevent the default point from drawing for this series
          // We return explicitly here ONLY IF we don't want tooltips on the bars themselves
          // If tooltips ARE desired on the bars, remove the return and let the tooltip logic below run.
          // For now, let's assume we DON'T draw the original point, just the bar.
          // ctx.element.remove(); // Alternative way to remove the point if needed, but returning works too.
          // return; // Skip default point drawing and tooltip logic *for this point*
        }

        // Default animation for lines (series-0)
        if (ctx.type === "line" && ctx.seriesIndex !== 1) {
          // Verificação de segurança: garante que chartRect existe
          if (!ctx.chartRect || typeof ctx.chartRect.height !== 'function') {
            return; // Sai sem animar se não houver dados suficientes
          }

          ctx.element.animate({
            d: {
              begin: 600,
              dur: 1000, // Reduced duration slightly
              from: ctx.path
                .clone()
                .scale(1, 0)
                .translate(0, ctx.chartRect.height())
                .stringify(),
              to: ctx.path.clone().stringify(),
              easing: Chartist.Svg.Easing.easeOutQuint, // Use Chartist's easing
            },
          });
        }

        // Default animation and tooltip logic for points (series-0)
        // Tooltips will also attach to series-1 points unless explicitly prevented above
        if (ctx.type === "point") {
          // Animate points (optional, can keep for series-0)
          if (ctx.seriesIndex !== 1) {
            // Only animate points for non-bar series
            ctx.element.animate({
              opacity: {
                begin: (ctx.index + 1) * 80,
                dur: 500, // Reduced duration
                from: 0,
                to: 1,
                easing: "ease",
              },
            });
          }

          // --- Tooltip Logic ---
          // Get data for the tooltip
          const pointIndex = ctx.index;
          const label = dataConfig.labels?.[pointIndex];
          // Access series data safely using optional chaining
          const metaValue = dataConfig.series?.[0]?.[pointIndex]; // Series 0: Meta
          const realValue = dataConfig.series?.[1]?.[pointIndex]; // Series 1: Real

          // Build tooltip content dynamically
          let tooltipContent = label ? `<strong>${label}</strong><br/>` : "";
          const metaName =
            dataConfig.seriesNames?.[0] || t("whatshappening.labels.meta");
          const realName =
            dataConfig.seriesNames?.[1] || t("whatshappening.labels.real");

          // Check if metaValue is valid (not null/undefined) before adding
          if (metaValue !== null && metaValue !== undefined) {
            tooltipContent += `${metaName}: ${formatNumber(metaValue)}<br/>`;
          }
          // Check if realValue is valid before adding
          if (realValue !== null && realValue !== undefined) {
            tooltipContent += `${realName}: ${formatNumber(realValue)}<br/>`;

            // Calculate ratio only if both meta and real are valid and meta > 0
            if (
              metaValue !== null &&
              metaValue !== undefined &&
              metaValue > 0
            ) {
              const ratio = ((realValue / metaValue) * 100).toFixed(1);
              tooltipContent += `${t("whatshappening.tooltip.real_meta_ratio")} ${ratio}%`;
            }
          }

          // Attach tooltip using Tippy only if there's content
          if (
            tooltipContent &&
            tooltipContent !== `<strong>${label}</strong><br/>`
          ) {
            // Ensure more than just the label exists
            // Check if a tippy instance already exists, update content; otherwise, create new
            if (ctx.element?._node?._tippy) {
              ctx.element._node._tippy.setContent(tooltipContent);
            } else if (ctx.element?._node) {
              // Ensure node exists before creating tippy
              tippy(ctx.element._node, {
                content: tooltipContent,
                allowHTML: true,
                placement: "top",
                theme: "light", // Or your preferred theme
                arrow: true,
              });
            }
          }
          // --- End Tooltip Logic ---
        }
      },
      // 'created' event can be used for things after the chart structure is ready
      // created: (ctx) => {
      //   // Example: You could potentially find the zero line position here if needed
      // }
    }),
    [dataConfig, t] // Include 't' in dependencies as it's used inside
  );

  function renderLegend() {
    if (!dataConfig.seriesNames || dataConfig.seriesNames.length === 0) {
      return null;
    }
    return (
      <div className={classes.legendContainer}>
        {dataConfig.seriesNames.map((name, idx) => {
          if (idx === 2) return null; // Skip projection legend item
          let color = "#0B91B5"; // default for series-0
          if (idx === 1) color = "#28A745";
          // if (idx === 2) color = "#FF9800"; // Projection color (commented out)
          return (
            <div key={idx} className={classes.legendItem}>
              <div style={{ width: 8, height: 8, backgroundColor: color }} />
              <span>{name}</span>
            </div>
          );
        })}
      </div>
    );
  }

  // If dev reports: thresholdingsconfig = {Greenrange: [...], yellowrange: [...], redrange: [...]}
  // Or ThresHoldSconFig = {ASC: {...}, Desc: {...}, mod: {...}}, Extracting by Key

  function getProgressColor(ratio, trackingMode, thresholds) {
    const mode = trackingMode?.toUpperCase();

    // 1) Try to find thresholds [Mode] (if it's multi-leal)

    let cfg = thresholds?.[mode];
    // 2) If you didn't find it, it assumes that Thresholds is the object of ranges

    if (!cfg) {
      cfg = thresholds;
    }

    // If you Still Don't have Greenrange, defines Fallback

    if (!cfg || !cfg.greenRange) {
      if (mode === "ASC") {
        cfg = {
          greenRange: [1.0, Infinity],
          yellowRange: [0.9, 1.0],
          redRange: [0, 0.9],
        };
      } else if (mode === "DESC") {
        cfg = {
          greenRange: [0, 0.8],
          yellowRange: [0.8, 1.0],
          redRange: [1.0, Infinity],
        };
      } else if (mode === "MOD") {
        cfg = {
          greenRange: [0.95, 1.05],
          yellowRangeLow: [0.9, 0.95],
          yellowRangeHigh: [1.05, 1.1],
          redRange: [0, 99999],
        };
      } else {
        // Total Fallback

        cfg = {
          greenRange: [1.0, Infinity],
          yellowRange: [0.9, 1.0],
          redRange: [0, 0.9],
        };
      }
    }

    const inRange = (val, [min, max]) => val >= min && val <= max;

    switch (mode) {
      case "ASC":
      case "DESC": {
        // WE EXPECT {GREENRANGE, YELLOWRANGE, RORNGE}

        if (inRange(ratio, cfg.greenRange)) return "#4caf50";
        if (inRange(ratio, cfg.yellowRange)) return "#ffeb3b";
        return "#f44336";
      }
      case "MOD": {
        // We Expect {Greenrange, Yellowrangelow, Yellowrangehigh, Redrange}

        if (inRange(ratio, cfg.greenRange)) return "#4caf50";
        if (
          (cfg.yellowRangeLow && inRange(ratio, cfg.yellowRangeLow)) ||
          (cfg.yellowRangeHigh && inRange(ratio, cfg.yellowRangeHigh))
        ) {
          return "#ffeb3b";
        }
        return "#f44336";
      }
      default:
        // fallback
        // If it is not "ASC", "Des Des" or "mod", assumes ASC

        if (inRange(ratio, cfg.greenRange)) return "#4caf50";
        if (inRange(ratio, cfg.yellowRange)) return "#ffeb3b";
        return "#f44336";
    }
  }

  // Reaching (Ratio = Real /Goal) Calculation

  const realArr = dataConfig.series?.[1] || [];
  const metaArr = dataConfig.series?.[0] || [];
  let ratio = 0;
  let realVal = 0;
  let metaVal = 0;

  // Finds the last index (from the end to the beginning) with real and meta> 0

  let foundIndex = -1;
  for (let i = metaArr.length - 1; i >= 0; i--) {
    const mv = metaArr[i];
    const rv = realArr[i];
    if (
      mv !== null &&
      mv !== undefined &&
      mv > 0 &&
      rv !== null &&
      rv !== undefined &&
      rv > 0
    ) {
      foundIndex = i;
      break;
    }
  }
  if (foundIndex !== -1) {
    realVal = realArr[foundIndex];
    metaVal = metaArr[foundIndex];
    ratio = realVal / metaVal;
  }

  const atingimento = Math.round(ratio * 100);
  const progressColor = getProgressColor(ratio, trackingMode, thresholdsConfig);

  // function to deal with more clean colors for table

  function pastelColor(baseColor) {
    switch (baseColor) {
      case "#4caf50":
        return "#C8E6C9";
      case "#ffeb3b":
        return "#FFF9C4";
      case "#f44336":
        return "#FFCDD2";
      default:
        return "#fff";
    }
  }

  function renderChart() {
    return (
      <div className={classes.chartSection}>
        <div className={classes.chartArea}>
          <ChartistGraph
            key={`${timeAgg}-${viewMode}`}
            data={chartData}
            type="Line"
            options={chartOptions}
            listener={animation}
            style={{ height: "100%" }}
            className="my-custom-chart"
          />
        </div>
        {renderLegend()}
      </div>
    );
  }

  function renderTable() {
    const { labels, series, seriesNames } = dataConfig;
    if (!labels || !series || series.length < 2)
      return <div>{t("whatshappening.messages.no_data")}</div>;

    const metaValues = series[0] || [];
    const realValues = series[1] || [];
    //const projValues = series[2] || [];
    const metaName = seriesNames?.[0] || t("whatshappening.labels.meta");
    const realName = seriesNames?.[1] || t("whatshappening.labels.real");
    //const projName = seriesNames?.[2] || t("whatshappening.labels.projection");

    return (
      <div className={classes.tableContainer}>
        <table className={classes.kpiTable}>
          <thead>
            <tr>
              <th className={classes.kpiTableHeaderCell} />
              {labels.map((lbl, idx) => (
                <th key={idx} className={classes.kpiTableHeaderCell}>
                  {lbl}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {/* Row 1 = Goal */}
            <tr>
              <td className={`${classes.kpiTableCell} ${classes.rowHeader}`}>
                {metaName}
              </td>
              {metaValues.map((val, i) => (
                <td key={i} className={classes.kpiTableCell}>
                  {val != null ? formatNumber(val) : "--"}
                </td>
              ))}
            </tr>
            {/* Row 2 = Real */}
            <tr>
              <td className={`${classes.kpiTableCell} ${classes.rowHeader}`}>
                {realName}
              </td>
              {realValues.map((val, i) => (
                <td key={i} className={classes.kpiTableCell}>
                  {val != null ? formatNumber(val) : "--"}
                </td>
              ))}
            </tr>
            {/* Row 3 = Projection (only if it exists) 
            {projValues.length ? (
              <tr>
                <td className={`${classes.kpiTableCell} ${classes.rowHeader}`}>
                  {projName}
                </td>
                {projValues.map((val, i) => {
                  const rv = realValues[i];
                  // If Real already exists (> 0), we force projection to null
                  const displayVal = rv && rv > 0 ? null : val;
                  return (
                    <td key={i} className={classes.kpiTableCell}>
                      {displayVal != null ? formatNumber(displayVal) : "--"}
                    </td>
                  );
                })}
              </tr>
            ) : null}
             */}
            {/* Row 4 = Comparison/Goal% */}
            <tr>
              <td className={`${classes.kpiTableCell} ${classes.rowHeader}`}>
                {t("whatshappening.labels.comparison_goal_percent")}
              </td>
              {labels.map((_, i) => {
                const mv = metaValues[i];
                const rv = realValues[i];
                //const pv = projValues[i];

                // We'll decide Which Value to Use For Ratio: Realval If> 0, if not Projval

                let val = 0;
                if (mv && mv > 0) {
                  if (rv && rv > 0) {
                    val = rv;
                  } else if (pv && pv > 0) {
                    val = pv;
                  }
                }

                if (mv && mv > 0 && val > 0) {
                  const ratioTmp = val / mv;
                  const ratioPercent = (ratioTmp * 100).toFixed(1);
                  // We use GetPress Color to Find Base Color (#4caf50, etc.)

                  const baseColor = getProgressColor(
                    ratioTmp,
                    trackingMode,
                    thresholdsConfig
                  );
                  // We transformed into pastel

                  const bgColor = pastelColor(baseColor);

                  return (
                    <td
                      key={i}
                      style={{
                        backgroundColor: bgColor,
                      }}
                      className={classes.kpiTableCell}
                    >
                      {ratioPercent}%
                    </td>
                  );
                }
                return (
                  <td key={i} className={classes.kpiTableCell}>
                    --
                  </td>
                );
              })}
            </tr>
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className={classes.container}>
      <div className={classes.contentWrapper}>
        {viewMode === "chart" ? renderChart() : renderTable()}

        {/* The circular gauge with the reaching */}
        <div className={classes.progressSection}>
          <Tippy
            content={`${t("whatshappening.labels.meta")} ${formatNumber(metaVal)} | ${t("whatshappening.labels.real")} ${formatNumber(realVal)}`}
            arrow
            theme="light"
          >
            <div style={{ width: 100, height: 100 }}>
              <CircularProgressbar
                value={atingimento}
                text={`${atingimento}%`}
                styles={buildStyles({
                  pathColor: progressColor,
                  textSize: "25px",
                  textColor: "#333",
                  trailColor: "#ddd",
                })}
              />
            </div>
          </Tippy>
        </div>
      </div>
    </div>
  );
}

MultiSeriesLineChart.propTypes = {
  timeAgg: PropTypes.string.isRequired,
  viewMode: PropTypes.string.isRequired,
  data: PropTypes.shape({
    labels: PropTypes.arrayOf(PropTypes.string),
    series: PropTypes.arrayOf(PropTypes.array),
    seriesNames: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  trackingMode: PropTypes.string.isRequired,
  thresholdsConfig: PropTypes.object.isRequired,
};

export default React.memo(MultiSeriesLineChart);
